import React, {useState, useEffect} from 'react'
import { Box, ButtonBase, Typography, Paper, Button, Accordion, AccordionSummary, AccordionDetails, Snackbar } from '@mui/material'
import FavoriteIcon from '@mui/icons-material/Favorite';
import { useNavigate } from 'react-router-dom';
import "react-color-palette/css";
import ColorPickerPopup from './ColorPickerPopup';
import { useDispatch, useSelector } from 'react-redux';
import { SvgIcon } from '@mui/material';
import { DEFAULT_HEART_COLOR_NUMBER, EMPTY_GROUP_ID, MQTT_TOPIC_PREFIX } from '../constants';
import { numberToIColor, rgbToColorNumber } from '../validation/utils';
import { publishMessage } from '../api/mqtt';
import { BACKGROUND_COLOR, DISABLED_BUTTON_GRAY, ICON_COLOR, PRIMARY_BUTTON_BLUE } from '../colorPalette';
import { updateGroupOnReduxAndServer } from '../redux/groupSlice';
import ColorPickerIcon from './ColorPickerIcon';
import Alert from '@mui/material/Alert';
import SettingsIcon from '@mui/icons-material/Settings';
import { subscribeToTopics } from '../api/mqtt';
import { GroupSchema } from 'lumas-shared-schemas';
import { createStatusListener } from '../api/mqtt-handlers';


function HeartContainer({id, ...props}) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [popupOpen, setPopupOpen] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    
    const heart = useSelector((state) => state.heart.hearts.find((heart) => heart.id === id));
    const group = useSelector((state) => state.group.groups.find((group) => group.id === heart.group));
    const myGroupColor = group?.color;
    const subscribedTopics = useSelector((state) => state.mqtt.subscribedTopics); // avoid resubscribing to topics
    
    // Set heart to default color, will update to group color if heart is in a group
    const [heartColorHex, setHeartColorHex] = useState(numberToIColor(DEFAULT_HEART_COLOR_NUMBER).hex); // only save the current heart color hex to state


    // useEffect hook to subscribe to status topic for all hearts
      useEffect(() => {
        try {
            if(group) {
                const statusTopic = MQTT_TOPIC_PREFIX + group.id + "/onlineStatus"
                const topics = [statusTopic]
                // Create MQTT listeners outside component to avoid re-renders
                const listener = createStatusListener(heart, dispatch, id);
                const cleanup = subscribeToTopics(topics, dispatch, listener, subscribedTopics);
    
                // Remove listener on unmount
                return cleanup;
            }
        }
        catch (err) {
            console.error("Error subscribing to onlineStatus topic for each heart:", err);
        }
      }, []); // Note: only run once on heart mount

    // useEffect hook to update the color of the heart when the group color changes
    useEffect(() => {
        if (!group) return;

        setHeartColorHex(numberToIColor(myGroupColor).hex);
        console.log("Group color updated to", myGroupColor, "with hex code", heartColorHex, "for heart", heart.name);
        
    }, [myGroupColor]);

    const handleButtonClick = () => {
        if (heart.group !== EMPTY_GROUP_ID && heart.group !== undefined) {
            setPopupOpen(true);
        } else {
            setAlertOpen(true);
        }
    };

    const handleSave = (newHex, newRgb) => {
        setHeartColorHex(newHex);
        const colorNumber = rgbToColorNumber(newRgb.r, newRgb.g, newRgb.b);

        const colorMessage = `${colorNumber},${heart.id},WEB_APP`;
        publishMessage(MQTT_TOPIC_PREFIX + heart.group + "/color", colorMessage);

        const updatedGroup = GroupSchema.parse({ 
            ...group, 
            color: colorNumber 
        });
        dispatch(updateGroupOnReduxAndServer({ id: heart.group, updatedGroup }));
        setPopupOpen(false);
    };

    const handleCancel = () => {
        setPopupOpen(false); 
    };

    const heartContainerStyles = {
        justifyContent: 'center',
        alignItems: 'center',
    }

    const handleCloseAlert = () => {
        setAlertOpen(false);
    }

    return (
        <div>
            {/* Alert popup */}
            <Snackbar
                open={alertOpen}
                autoHideDuration={3000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
                    You must add the heart to a group to change its color.
                </Alert>
            </Snackbar>

            {popupOpen && (
                <ColorPickerPopup title={"Pick a color:"} initialColor={heartColorHex} handleSave={handleSave} handleCancel={handleCancel} />
            )}
            <Box sx={{width: '100%', borderRadius: '10px', display: 'flex', marginTop: '15px', alignItems: 'center', flexDirection: 'column', backgroundColor: BACKGROUND_COLOR}}>
                <Box sx={{marginTop: '10px', flexDirection: 'row', display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between'}}>
                    <Box sx={{minWidth: '30px'}}>
                        <Box sx={{backgroundColor: heart.status === 'Online' ? '#03AA00' : '#FF0000', width: '20px', 
                                height: '20px', 
                                borderRadius: '10px', 
                                border: "1px solid black", position: 'relative',
                                left: '10px'}} />
                    </Box>
                    <Typography sx={{flexGrow: 1, textAlign: 'center'}} variant='body1' color={'black'} fontWeight={'small'}>{heart.name}</Typography>
                    <ButtonBase sx={{ right: '10px'}} onClick={() => {navigate(`/manage-heart/${id}`)}}>
                        <SettingsIcon sx={{fontSize: '30px', color: ICON_COLOR}} />
                    </ButtonBase>
                </Box>
                <ButtonBase sx={heartContainerStyles} onClick={handleButtonClick}>
                    {/* This needs to be behind the SvgIcon because the transform doesn't actually scale the width and height, so the ButtonBase doesn't expand without this line */}
                    <FavoriteIcon sx={{fontSize: '300px', color: 'white', fill: heartColorHex}}/>
                    {/* Using SvgIcon instead of FavoriteIcon to draw border: https://stackoverflow.com/questions/53529640/how-to-add-an-outline-stroke-around-a-material-ui-icon */}
                    <SvgIcon sx={{transform: 'scale(12.5)', position: 'absolute'}}>
                        <path
                            d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                            stroke="black"
                            strokeWidth={0.5}
                            fontSize={100}
                            color={'white'}
                            fill={heartColorHex}
                        />
                    </SvgIcon>
                    <ColorPickerIcon customStyles={{position: 'absolute', right: '80px', bottom: '40px',}} customIconStyles={{ backgroundColor: heart.group === EMPTY_GROUP_ID ? DISABLED_BUTTON_GRAY : PRIMARY_BUTTON_BLUE}} />
                </ButtonBase>

                {/* Add an MUI Accordion to list the heart's group's name (if any), and when expanded, the group's members */}
                <Box sx={{paddingTop: '5px', paddingBottom: '10px', width: '100%', display: 'flex', justifyContent: 'center'}}>
                    {heart.group === EMPTY_GROUP_ID ? 
                        <Button variant="contained" color="primary" onClick={() => {navigate(`/manage-heart/${id}/add-to-group`)}}>
                            Add To Group
                        </Button> 
                        :
                        <Box sx={{textAlign: 'left', width: '100%', paddingLeft: '15px', display: 'flex', flexDirection: 'row', marginBottom: '15px'}}>
                            <Typography variant='body2' fontWeight={"bold"} color={'black'}>Group:</Typography>
                            <Typography variant='body2' color={'black'} marginLeft={'10px'}>{group === undefined ? EMPTY_GROUP_ID : group.name}</Typography>
                        </Box>
                    }   
                </Box>
            </Box>
        </div>
    )
}

export default HeartContainer